// Bounded log ring (docs/09 §7, docs/10 §2): the lines the brain sent through
// the host log or `console.*`, so a developer screen — and a field report — can
// read the brain's own view of a session without a cable. `dev.logs` returns it;
// the `log` channel streams it live while dev mode is unlocked.
//
// Two properties this ring did not have on 2026-07-26, and the whole reason the
// field incident's evidence base was four photographed lines:
//
//   * it is bounded by BYTES as well as by count, so one pathological line
//     cannot evict a session's worth of context (and so the persisted copy has
//     a size a KV store can be promised);
//   * it SURVIVES A COLD START, through `diag/ring.ts` — the incident began at
//     app launch precisely because everything before it was gone, and a crash
//     loop is exactly the case where the interesting lines are the lost ones.
//
// Log hygiene is enforced AT THE SOURCE (errKind discipline — codes and kinds,
// never message bodies, tokens, ids). This module stores what it is given: it
// must never become the place where a raw error body is considered "safe
// because it is only local". The structured `event` half is different in kind:
// it is the ONLY half that is ever uploaded, and everything on it has already
// been through `diag/redact.ts` by the time it arrives.

import type { DiagEvent } from './diag/events.js';

export interface LogLine {
  /** Date.now() at log time. Device-local and precise; a report coarsens it. */
  ts: number;
  level: string;
  msg: string;
  /**
   * Analysable payload (docs/10 §2). Optional: most lines are prose for a
   * human, and prose is never uploaded.
   */
  event?: DiagEvent;
}

/** Matches the iOS host's own ring, so both halves of a session line up. */
export const LOG_RING_SIZE = 300;

/**
 * Second cap, on the serialized size. 64 KB is comfortably under what any of
 * the three KV backings treats as a large value, and it is what makes the
 * persisted ring's cost predictable regardless of what got logged.
 */
export const LOG_RING_MAX_BYTES = 64_000;

/**
 * How much of the ring the structured half may occupy before eviction starts
 * preferring it. Every REST request now leaves an `http.attempt` behind, which
 * is an order of magnitude chattier than the prose — without this the ring is
 * pure diagnostics after a few hundred requests, and rung 5 (docs/10 §5: the
 * user reading and copying a report, the only rung that always works) is the
 * half that gets evicted. Events are the uploadable half; prose is the half a
 * person reads, and neither may starve the other.
 */
export const LOG_RING_EVENT_SHARE = 0.75;

/** Notified after every push, so the persistence layer can coalesce writes.
 *  The LEVEL travels with it: an `error` line is written through immediately
 *  (diag/ring.ts), because the lines that matter to a crash are the ones a
 *  coalescing window is most likely to be holding when it happens. */
export interface RingPersistence {
  note(level?: string): void;
}

const ring: LogLine[] = [];
const costs: number[] = [];
let bytes = 0;
/** Lines carrying a structured payload — the quantity LOG_RING_EVENT_SHARE
 *  bounds. Maintained incrementally; recomputing it per push is O(n) per line. */
let eventLines = 0;
let sink: ((line: LogLine) => void) | null = null;
let persistence: RingPersistence | null = null;

/** UTF-8 length of a string, without TextEncoder (absent in a bare JSC context
 *  until polyfills load, and this module is loaded by them). */
function utf8Length(text: string): number {
  let n = 0;
  for (let i = 0; i < text.length; i += 1) {
    const c = text.charCodeAt(i);
    if (c < 0x80) n += 1;
    else if (c < 0x800) n += 2;
    else if (c >= 0xd800 && c <= 0xdbff) {
      n += 4; // surrogate pair — one 4-byte code point
      i += 1;
    } else n += 3;
  }
  return n;
}

function costOf(line: LogLine): number {
  return utf8Length(JSON.stringify(line));
}

/** Index of the oldest structured line, or 0 when there is none. */
function oldestEventIndex(): number {
  for (let i = 0; i < ring.length; i += 1) {
    if (ring[i]?.event !== undefined) return i;
  }
  return 0;
}

/**
 * Drop exactly one line: the oldest, EXCEPT while the structured half is over
 * its share, when the oldest STRUCTURED line goes instead. Relative order is
 * untouched either way — this chooses which line leaves, never where the rest
 * sit.
 */
function dropOne(): void {
  const index = eventLines > ring.length * LOG_RING_EVENT_SHARE ? oldestEventIndex() : 0;
  const [line] = ring.splice(index, 1);
  const [cost] = costs.splice(index, 1);
  bytes -= cost ?? 0;
  if (line?.event !== undefined) eventLines -= 1;
}

/** Eviction until BOTH caps hold. */
function evict(): void {
  while (ring.length > LOG_RING_SIZE || (bytes > LOG_RING_MAX_BYTES && ring.length > 1)) {
    dropOne();
  }
  if (bytes < 0) bytes = 0;
}

function admit(line: LogLine): void {
  ring.push(line);
  costs.push(costOf(line));
  bytes += costs[costs.length - 1] as number;
  if (line.event !== undefined) eventLines += 1;
  evict();
}

/** Record a line (and stream it when a sink is armed). */
export function pushLog(level: string, msg: string, event?: DiagEvent): void {
  const line: LogLine = event === undefined ? { ts: Date.now(), level, msg } : { ts: Date.now(), level, msg, event };
  admit(line);
  if (persistence) {
    try {
      persistence.note(level);
    } catch {
      // Storage must never break the code that logged.
    }
  }
  if (sink) {
    try {
      sink(line);
    } catch {
      // A failing stream must never break the code that logged.
    }
  }
}

/** Oldest → newest. Copy: callers serialize it, the ring keeps mutating. */
export function logRing(): LogLine[] {
  return ring.slice();
}

/** Serialized size of the ring right now — the quantity `LOG_RING_MAX_BYTES`
 *  bounds, exposed so the cap is observable rather than merely asserted. */
export function logRingBytes(): number {
  return bytes;
}

/**
 * Seed the ring with lines recovered from storage at boot (`diag/ring.ts`).
 * They go in FRONT of whatever this process has already logged — a cold start
 * emits its first lines before KV answers — and the caps are then applied to
 * the merged sequence, so a restored session cannot push out the live one.
 */
export function hydrateLogRing(restored: readonly LogLine[]): void {
  if (restored.length === 0) return;
  const live = ring.slice();
  const liveCosts = costs.slice();
  ring.length = 0;
  costs.length = 0;
  bytes = 0;
  eventLines = 0;
  for (const line of restored) {
    if (typeof line?.ts !== 'number' || typeof line.level !== 'string' || typeof line.msg !== 'string') {
      continue;
    }
    ring.push(line);
    const cost = costOf(line);
    costs.push(cost);
    bytes += cost;
    if (line.event !== undefined) eventLines += 1;
  }
  for (let i = 0; i < live.length; i += 1) {
    const line = live[i] as LogLine;
    ring.push(line);
    const cost = liveCosts[i] ?? costOf(line);
    costs.push(cost);
    bytes += cost;
    if (line.event !== undefined) eventLines += 1;
  }
  evict();
}

export function clearLogRing(): void {
  ring.length = 0;
  costs.length = 0;
  bytes = 0;
  eventLines = 0;
}

/**
 * Arm/disarm live streaming (the `log` channel). Only ever armed while dev mode
 * is unlocked — the channel is a developer tool, not a telemetry pipe.
 */
export function setLogSink(next: ((line: LogLine) => void) | null): void {
  sink = next;
}

export function isLogStreaming(): boolean {
  return sink !== null;
}

/** Attach (or detach) the KV write-behind. One at a time: the ring is a
 *  process singleton and so is its storage slot. */
export function setRingPersistence(next: RingPersistence | null): void {
  persistence = next;
}

interface ConsoleLike {
  log?: (...args: unknown[]) => void;
  info?: (...args: unknown[]) => void;
  debug?: (...args: unknown[]) => void;
  warn?: (...args: unknown[]) => void;
  error?: (...args: unknown[]) => void;
  trace?: (...args: unknown[]) => void;
  __merkuryCaptured?: boolean;
}

const CONSOLE_LEVELS: [keyof ConsoleLike, string][] = [
  ['log', 'info'],
  ['info', 'info'],
  ['debug', 'debug'],
  ['warn', 'warn'],
  ['error', 'error'],
  ['trace', 'debug'],
];

function fmt(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v instanceof Error) return `${v.name}: ${v.message}`;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

/**
 * Mirror `console.*` into the ring, then delegate to the original method (which
 * on the JSC host is already the polyfill forwarding to `__host.log`). Idempotent
 * — a second call on the same console object is a no-op.
 */
export function captureConsole(target?: ConsoleLike): void {
  const target_ = target ?? (globalThis as { console?: ConsoleLike }).console;
  if (!target_ || target_.__merkuryCaptured === true) return;
  for (const [method, level] of CONSOLE_LEVELS) {
    const original = target_[method] as ((...args: unknown[]) => void) | undefined;
    if (typeof original !== 'function') continue;
    (target_ as Record<string, unknown>)[method] = (...args: unknown[]): void => {
      pushLog(level, args.map(fmt).join(' '));
      original.apply(target_, args);
    };
  }
  target_.__merkuryCaptured = true;
}
